// Canonical destinations, legacy links and safe parent fallbacks shared by mobile, desktop and Android navigation.
export const PRIMARY_DESTINATIONS = [
  { id: 'home', path: '/home', zh: '首页', en: 'Home' },
  { id: 'ledger', path: '/ledger', zh: '账本', en: 'Ledger' },
  { id: 'finance', path: '/finance', zh: '财务', en: 'Finance' },
  { id: 'insights', path: '/insights', zh: '分析', en: 'Insights' },
  { id: 'assistant', path: '/assistant', zh: '管家', en: 'Butler' },
] as const;
export type DestinationId = typeof PRIMARY_DESTINATIONS[number]['id'];
const within = (path: string, root: string) => path === root || path.startsWith(`${root}/`);
export function destinationFor(path: string): DestinationId | null {
  if (within(path, '/advisor')) return path.startsWith('/advisor/plan') || path.startsWith('/advisor/history') ? 'assistant' : 'finance';
  if (within(path, '/receipts') || within(path, '/shopping') || path === '/price-check' || within(path, '/capture')) return 'ledger';
  if (path === '/tasks') return 'home';
  return PRIMARY_DESTINATIONS.find(item => within(path, item.path))?.id ?? null;
}
export const isMainDestination = (path: string) => PRIMARY_DESTINATIONS.some(item => item.path === path);
export const showsMainNavigation = (path: string) => !['/auth', '/join', '/onboarding', '/capture', '/__ui', '/mcp/authorize', '/assistant/chat', '/ledger/reconcile/upload'].some(root => within(path, root));
export function safeReturnTo(value: unknown): string | null {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !/[\\\u0000-\u001f]/.test(value) ? value : null;
}
/** Null means canonical; preserve all unrelated query state through compatibility redirects. */
export function resolveLegacyLocation(pathname: string, search = '', hash = ''): string | null {
  const params = new URLSearchParams(search);
  let next: string | undefined;
  let nextHash = hash;
  if (pathname === '/home' && ['from', 'to', 'category_id', 'recorded_by', 'spender_user_id', 'q', 'status', 'kind'].some(key => params.has(key))) {
    next = '/ledger';
    // Old receipt links searched all dates unless they explicitly included a range.
    if (!params.get('from') && !params.get('to') && !params.has('date_scope')) params.set('date_scope', 'all');
  }
  if (pathname === '/advisor') {
    next = params.get('tab') === 'plan' ? '/assistant/plans' : params.get('tab') === 'history' ? '/assistant/history' : '/finance';
    params.delete('tab');
  } else if (pathname === '/advisor/setup/cashflow') {
    const sections: Record<string, string> = { income: 'income', fixed: 'bills', 'fixed-expenses': 'bills', budgets: 'budgets' };
    next = sections[hash.slice(1)] ? `/finance/${sections[hash.slice(1)]}` : '/finance'; nextHash = '';
  } else if (pathname === '/advisor/setup/wealth') {
    const sections: Record<string, string> = { assets: 'assets', liabilities: 'liabilities', fi: 'independence', tax: 'tax' };
    next = `/finance/${sections[hash.slice(1)] ?? 'net-worth'}`; nextHash = '';
  } else {
    const prefixes = [['/advisor/setup/wealth/assets', '/finance/assets'], ['/advisor/setup/savings', '/finance/savings'], ['/advisor/setup/goals', '/finance/goals'], ['/advisor/setup/tax', '/finance/tax'], ['/advisor/plan', '/assistant/plans'], ['/advisor/history', '/assistant/history']];
    const prefix = prefixes.find(([old]) => within(pathname, old));
    if (prefix) next = prefix[1] + pathname.slice(prefix[0].length);
  }
  if (pathname === '/assistant') {
    const tab = params.get('tab');
    if (tab === 'progress') next = '/assistant/progress';
    else if (tab === 'savings' || tab === 'opportunities') next = '/assistant/opportunities';
    else if (tab === 'chat' || ['message', 'scope', 'session', 'conversation'].some(key => params.has(key))) next = '/assistant/chat';
    if (next) params.delete('tab');
  }
  if (pathname === '/insights') {
    if (params.get('group_id')) { next = `/ledger/groups/${encodeURIComponent(params.get('group_id')!)}`; params.delete('group_id'); }
    else if (params.get('tab') === 'reconcile') next = '/ledger/reconcile';
    else if (params.get('tab') === 'details') next = '/insights/details';
    if (next) params.delete('tab');
  }
  if (pathname === '/capture') {
    const modes: Record<string, string> = { single: '/capture/receipt', batch: '/capture/batch', cash: '/capture/manual', savings: '/capture/savings', bank: '/ledger/reconcile/upload' };
    next = modes[params.get('mode') ?? '']; if (next) params.delete('mode');
  }
  if (!next) return null;
  return next + (params.size ? `?${params}` : '') + nextHash;
}
export function parentPath(path: string): string {
  const legacy = resolveLegacyLocation(path); if (legacy) return parentPath(legacy.split('?')[0]);
  if (path === '/categories' || path === '/aliases') return '/settings/data';
  if (path === '/workspace' || within(path, '/settings')) return path === '/settings' ? '/home' : '/settings';
  if (within(path, '/receipts')) return '/ledger';
  if (path === '/price-check') return '/shopping';
  if (path === '/shopping') return '/ledger';
  if (path === '/assistant/history') return '/assistant/plans';
  if (path === '/tasks') return '/home';
  if (path === '/capture') return '/ledger';
  const slash = path.lastIndexOf('/'); return slash > 0 ? path.slice(0, slash) : '/home';
}
/** Persisted alerts can still carry the old all-in-one Home URL. */
export function alertDestination(type: string, url: string | null): string | null {
  const safe = safeReturnTo(url);
  if (!safe) return null;
  if (safe === '/home') {
    if (['pending_overdue', 'income_missed', 'fixed_due_today'].includes(type)) return '/finance/calendar';
    if (type === 'duplicate_receipt') return '/ledger?date_scope=all';
  }
  const parsed = new URL(safe, 'https://app.local');
  return resolveLegacyLocation(parsed.pathname, parsed.search, parsed.hash) ?? safe;
}
